import { initLocalization } from '@/__core__/localization/localization'
import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { defineStore } from 'pinia'
import { FALLBACK_EVENTS } from './FixturesWidget.constants'
import { allSettled } from '@/__core__/utils/fetch'

/** @helper */
const shouldScheduleRefresh = (events = []) => {
  // Check if there are no events scheduled within the next 3 hours
  // Get the current time
  const currentTime = new Date()

  // Calculate the time 3 hours from now
  const threeHoursFromNow = new Date()
  threeHoursFromNow.setHours(currentTime.getHours() + 3)

  // Check if any event starts between the current time and 3 hours from now
  const noEventsWithin3Hours = events.every(event => {
    const eventStartTime = new Date(event.startTime)
    return eventStartTime < currentTime || eventStartTime > threeHoursFromNow
  })

  return events.filter(event => event.live).length > 0 || !noEventsWithin3Hours
}

/** @typedef {{ seasonId: String, sportType: String, startDate: String, endDate: String, round: Number, roundIdentifier: String, languageIdentifier: String }} Payload */
export const useFixturesStore = defineStore('fixtures', {
  state: () => ({
    season: {
      id: '',
      name: '',
      year: ''
    },
    competition: {
      id: '',
      logoUrl: '',
      name: ''
    },
    events: FALLBACK_EVENTS,
    drfRetryCounter: 0
  }),

  getters: {
    getEvent: state => eventId => state.events.find(event => event.id === eventId),
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    }
  },

  actions: {
    getShouldCallApi() {
      return shouldScheduleRefresh(this.events)
    },
    /**
     * @param {Payload}
     * @returns {Promise<boolean>} - returns if refresh should be scheduled
     */
    async fetchFixturesData({ seasonId, sportType, startDate, endDate, round, roundIdentifier, region, languageIdentifier }) {
      const payload = {
        seasonId,
        sportType,
        languageIdentifier,
        region,
        /* round has more priority than date range, so if the round is present use it instead of date range */
        ...(round ? { round, roundIdentifier } : { startDate, endDate })
      }

      const API = payload.sportType === 'tennis' ? 'TennisFixtures' : 'Fixtures'

      try {
        const [, responseFixtures] = await allSettled([initLocalization(languageIdentifier), SportsModeServiceRepository[API].get(payload)])

        if (!responseFixtures) {
          throw new Error('Fixtures data not found')
        }

        return this.updateFixtures(responseFixtures.data)
      } catch (error) {
        throw new Error(`[Fixtures.store, fetchFixturesData]: ${error.message}`)
      }
    },
    /** @param {Payload} payload */
    async scheduleFixturesRefresh(payload) {
      const API = payload.sportType === 'tennis' ? 'TennisFixtures' : 'Fixtures'
      SportsModeServiceRepository[API].get(payload)
        .then(response => this.updateFixtures(response.data))
        .catch(() => {
          this.drfRetryCounter++
        })
    },
    updateFixtures(fixtures) {
      this.season = fixtures.season
      this.competition = fixtures.competition
      this.events = fixtures.events

      return {
        hasEvents: fixtures?.events?.length
      }
    }
  }
})
